import { computed, reactive, ref, watchEffect } from 'vue';

const SITE_SETTINGS_STORAGE_KEY = 'site_settings';

export type ThemeSetting = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

interface ThemeSettings {
  alerts: ThemeSetting;
  hub: ThemeSetting;
  issues: ThemeSetting;
}

interface WebsiteSettings {
  theme: ThemeSettings;
}

const osPrefersDark = ref(false);

export const availableThemes: { value: ThemeSetting; label: string }[] = [
  { label: 'Light', value: 'light' },
  { label: 'Dark', value: 'dark' },
  { label: 'Auto', value: 'system' },
];

function getInitialSiteSettings(): WebsiteSettings {
  const storedSettings = localStorage.getItem(SITE_SETTINGS_STORAGE_KEY);
  const defaultSiteSettings: WebsiteSettings = {
    theme: { alerts: 'system', hub: 'system', issues: 'system' },
  };

  if (storedSettings) {
    try {
      const parsed = JSON.parse(storedSettings) as Partial<WebsiteSettings>;
      const validatedSettings: WebsiteSettings = { ...defaultSiteSettings, theme: { ...defaultSiteSettings.theme } };

      if (parsed.theme) {
        const defaultThemeSettings = defaultSiteSettings.theme;
        (Object.keys(defaultThemeSettings) as Array<keyof ThemeSettings>).forEach((key) => {
          if (parsed.theme?.[key] && availableThemes.some(t => t.value === parsed.theme?.[key])) {
            validatedSettings.theme[key] = parsed.theme[key] as ThemeSetting;
          }
        });
      }

      return validatedSettings;
    }
    catch (e) {
      console.error('Failed to parse site settings from localStorage', e);
    }
  }
  return defaultSiteSettings;
}

export const siteSettings = reactive<WebsiteSettings>(getInitialSiteSettings());

export const currentThemeAlerts = computed<EffectiveTheme>(() => getEffectiveTheme(siteSettings.theme.alerts));
export const currentThemeHub = computed<EffectiveTheme>(() => getEffectiveTheme(siteSettings.theme.hub));
export const currentThemeIssues = computed<EffectiveTheme>(() => getEffectiveTheme(siteSettings.theme.issues));

export const isAlertsDarkTheme = computed<boolean>(() => currentThemeAlerts.value === 'dark');
export const isHubDarkTheme = computed<boolean>(() => currentThemeHub.value === 'dark');
export const isIssuesDarkTheme = computed<boolean>(() => currentThemeIssues.value === 'dark');

function getEffectiveTheme(setting: ThemeSetting): EffectiveTheme {
  if (setting === 'system') {
    return osPrefersDark.value ? 'dark' : 'light';
  }
  return setting;
}

function applySectionTheme(section: keyof ThemeSettings, theme: EffectiveTheme) {
  const root = document.documentElement;
  const darkClassName = `${section}-dark`;
  const attributeName = `data-theme-${section}`;

  root.setAttribute(attributeName, theme);
  if (theme === 'dark') {
    root.classList.add(darkClassName);
  }
  else {
    root.classList.remove(darkClassName);
  }
}

export function setThemeSetting(section: keyof ThemeSettings, newSetting: ThemeSetting) {
  if (availableThemes.some(t => t.value === newSetting)) {
    siteSettings.theme[section] = newSetting;
    localStorage.setItem(SITE_SETTINGS_STORAGE_KEY, JSON.stringify(siteSettings));
  }
  else {
    console.warn(`Invalid theme setting '${newSetting}' for section '${section}'`);
  }
}

export function initializeSettingsManager() {
  watchEffect(() => applySectionTheme('alerts', currentThemeAlerts.value));
  watchEffect(() => applySectionTheme('hub', currentThemeHub.value));
  watchEffect(() => applySectionTheme('issues', currentThemeIssues.value));

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleOsThemeChange = (event: MediaQueryListEvent | { matches: boolean }) => {
    osPrefersDark.value = event.matches;
  };

  osPrefersDark.value = mediaQuery.matches;
  mediaQuery.addEventListener('change', handleOsThemeChange);

  applySectionTheme('alerts', currentThemeAlerts.value);
  applySectionTheme('hub', currentThemeHub.value);
  applySectionTheme('issues', currentThemeIssues.value);
}

initializeSettingsManager();
